/**
 * Client-side image compression helper.
 *
 * Rationale: event volunteers snap 4-8 MB photos of receipts on their phone.
 * Uploading raw hurts them on venue Wi-Fi and inflates our object storage bill.
 * This resizes to at most `maxDim` on the long side and re-encodes as JPEG.
 *
 * Non-images (PDFs etc.) are passed through unchanged.
 * If the compressed blob is not smaller, the original file is returned.
 */

const DEFAULTS = {
  maxDim: 1600,
  quality: 0.85,
  minBytesForCompression: 500 * 1024, // don't bother below 500 KB
};

export async function compressImage(file, opts = {}) {
  if (!file) return file;
  if (!file.type || !file.type.startsWith("image/")) return file;
  if (file.type === "image/gif") return file; // preserve animation
  const { maxDim, quality, minBytesForCompression } = { ...DEFAULTS, ...opts };
  if (file.size < minBytesForCompression) return file;

  const dataUrl = await _fileToDataUrl(file);
  const img = await _dataUrlToImage(dataUrl);

  const longest = Math.max(img.width, img.height);
  if (longest <= maxDim && file.type === "image/jpeg" && file.size < 2 * 1024 * 1024) {
    // Already reasonably sized — skip.
    return file;
  }
  const scale = longest > maxDim ? maxDim / longest : 1;
  const targetW = Math.round(img.width * scale);
  const targetH = Math.round(img.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, targetW, targetH);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
  if (!blob || blob.size >= file.size) return file;

  const baseName = (file.name || "receipt").replace(/\.[^.]+$/, "");
  return new File([blob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
}

function _fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("read failed"));
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });
}

function _dataUrlToImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = () => reject(new Error("decode failed"));
    img.onload = () => resolve(img);
    img.src = url;
  });
}
